import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from 'react-query';
import { AxiosError } from 'axios';
import qs from 'query-string';

// api
import { client, Resource } from 'api';

type UsersInfiniteQueryParams = {
  search: string;
  isSystemAdmin: string;
  isStaff: string;
};

type UserQueryData = {
  results: User[];
  next: string | null;
  previous: string | null;
  total: number;
};

type UserQueryParams = {
  isSystemAdmin: boolean;
  search: string;
  page: number;
};

type UserData = {
  users: User[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  total: number;
};

export const useDeleteSelfAccountMutation = () => {
  return useMutation(() => client.delete(Resource.Account + '/self'));
};

export const useUsersInfiniteQuery = (
  queryParams: UsersInfiniteQueryParams,
  options: UseInfiniteQueryOptions<UserQueryData, AxiosError, User[]> = {}
) => {
  return useInfiniteQuery<UserQueryData, AxiosError, User[]>(
    [Resource.Account, queryParams],
    ({ pageParam = 1 }) =>
      client
        .get(Resource.Account, {
          params: { ...queryParams, page: pageParam },
        })
        .then(({ data }) => data),
    {
      select: data => ({
        ...data,
        pages: data.pages.map(page => page.results),
      }),
      getNextPageParam: lastPage => qs.parse(lastPage.next || '')?.page,
      ...options,
    }
  );
};

export const useUserMutation = (
  options: UseMutationOptions<User, AxiosError<{ msg: string }>, User>
) => {
  const queryClient = useQueryClient();

  return useMutation<User, AxiosError<{ msg: string }>, User>(
    ({ id, ...data }) =>
      client
        .put(`${Resource.Account}/${id}`, {
          ...data,
        })
        .then(({ data }) => data),
    {
      ...options,
      onSuccess: (data, variables, ...args) => {
        queryClient.invalidateQueries([
          Resource.Account,
          { isSystemAdmin: true },
        ]);

        options?.onSuccess && options.onSuccess(data, variables, ...args);
      },
    }
  );
};

export const useUserQuery = (params: UserQueryParams) => {
  return useQuery<UserQueryData, AxiosError, UserData>(
    [Resource.Account, params],
    () => client.get(Resource.Account, { params }).then(({ data }) => data),
    {
      select: data => ({
        users: data.results,
        hasNextPage: data.next !== null,
        hasPreviousPage: data.previous !== null,
        total: data.total,
      }),
    }
  );
};
